<template>
  <Dialog :open="isOpen" @update:open="handleOpenChange">
    <DialogContent class="max-w-2xl bg-white">
      <DialogHeader>
        <DialogTitle>Edit company</DialogTitle>
      </DialogHeader>

      <div class="space-y-4">
        <div class="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
          <div>
            <p class="text-sm font-medium text-gray-900">Featured</p>
            <p class="text-xs text-gray-500">Show this company in featured section</p>
          </div>
          <Switch v-model="isFeatured" />
        </div>

        <div>
          <label class="text-sm font-medium text-gray-900">Featured reason</label>
          <Textarea
            v-model="featuredReason"
            class="mt-1 bg-white"
            rows="3"
            placeholder="Add a short reason for featuring this company"
          />
        </div>

        <div>
          <label class="text-sm font-medium text-gray-900">Company job board</label>
          <Input
            v-model="jobBoardUrl"
            class="mt-1 bg-white"
            placeholder="https://careers.company.com"
          />
        </div>

        <div>
          <label class="text-sm font-medium text-gray-900">Application notes</label>
          <Textarea
            v-model="jobApplicationNotes"
            class="mt-1 bg-white"
            rows="4"
            placeholder="Tips or instructions for applying"
          />
        </div>
      </div>

      <DialogFooter class="mt-6">
        <button
          class="px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
          type="button"
          @click="handleOpenChange(false)"
        >
          Cancel
        </button>
        <button
          class="px-3 py-2 text-sm rounded-md bg-cosmicPurple text-white hover:bg-cosmicPurple/90"
          type="button"
          @click="handleSave"
        >
          Save
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { ReferringCompany } from '@/types/company'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

const props = defineProps<{
  isOpen: boolean
  company: ReferringCompany | null
}>()

const emit = defineEmits<{
  'update:isOpen': [value: boolean]
  save: [payload: {
    is_featured: boolean
    featured_reason: string | null
    job_board_url: string | null
    job_application_notes: string | null
  }]
}>()

const isFeatured = ref(false)
const featuredReason = ref('')
const jobBoardUrl = ref('')
const jobApplicationNotes = ref('')

watch(
  () => props.company,
  (company) => {
    isFeatured.value = company?.is_featured ?? false
    featuredReason.value = company?.featured_reason || ''
    jobBoardUrl.value = company?.job_board_url || ''
    jobApplicationNotes.value = company?.job_application_notes || ''
  },
  { immediate: true }
)

const handleOpenChange = (value: boolean) => {
  emit('update:isOpen', value)
}

const handleSave = () => {
  emit('save', {
    is_featured: isFeatured.value,
    featured_reason: featuredReason.value.trim() || null,
    job_board_url: jobBoardUrl.value.trim() || null,
    job_application_notes: jobApplicationNotes.value.trim() || null
  })
}
</script>
